import { useEffect, useRef, useState } from "react";

function useVisuallyLoading(isLoading: boolean, debounceStart = 0, minimumTimeout = 1000): boolean {
  // fake loading for at least [minimumTimeout] ms
  const [isVisuallyLoading, setVisuallyLoading] = useState(false);
  const visuallyLoadingTimeoutStartRef = useRef(0);
  const visuallyLoadingTimeoutStopRef = useRef(0);
  const timeoutStartRef = useRef<number>();

  useEffect(() => {
    if (isLoading) {
      // start the loading, debounced if set
      timeoutStartRef.current = Date.now();
      visuallyLoadingTimeoutStartRef.current = window.setTimeout(() => {
        setVisuallyLoading(true);
      }, debounceStart);
    } else {
      // reset start of the visual loading in case it did not start yet
      window.clearTimeout(visuallyLoadingTimeoutStartRef.current);

      if (isVisuallyLoading) {
        // stop the loading after the remaining time, if any
        const now = Date.now();
        const remainingTime = timeoutStartRef.current
          ? Math.max(0, minimumTimeout - (now - timeoutStartRef.current))
          : 0;
        visuallyLoadingTimeoutStopRef.current = window.setTimeout(() => {
          setVisuallyLoading(false);
        }, remainingTime);
      }
    }

    return () => {
      window.clearTimeout(visuallyLoadingTimeoutStartRef.current);
      window.clearTimeout(visuallyLoadingTimeoutStopRef.current);
    };
  }, [isLoading, isVisuallyLoading, debounceStart, minimumTimeout]);

  return isVisuallyLoading;
}

const useLongLoading = (isLoading: boolean, delay = 1500) => {
  const [isLoadingForLong, setLoadingForLong] = useState(false);
  const loadingTimeoutRef = useRef<number>();

  useEffect(() => {
    if (!isLoading) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = undefined;
      setLoadingForLong(false);
    } else if (loadingTimeoutRef.current == null) {
      loadingTimeoutRef.current = window.setTimeout(() => {
        setLoadingForLong(true);
      }, delay);
    }

    return () => {
      window.clearTimeout(loadingTimeoutRef.current);
    };
  }, [isLoading, delay]);

  return isLoadingForLong;
};

export { useLongLoading, useVisuallyLoading };
